import { motion, AnimatePresence } from "framer-motion";
import {
  FaStar,
  FaChevronLeft,
  FaChevronRight,
  FaQuoteLeft,
} from "react-icons/fa";
import { useState } from "react";
import testimonials from "../../data/testimonials.json";

export default function Testimonials() {
  const [index, setIndex] = useState(0);

  const prev = () =>
    setIndex((i) => (i === 0 ? testimonials.length - 1 : i - 1));

  const next = () =>
    setIndex((i) => (i === testimonials.length - 1 ? 0 : i + 1));

  return (
    <section className="relative bg-gradient-to-b from-slate-50 via-white to-gray-50 py-16 sm:py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-20 left-10 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <p className="uppercase tracking-widest text-xs sm:text-sm text-indigo-600 font-semibold mb-3">
            From our community
          </p>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-[#0b1b4d] leading-tight">
            What learners say about Ekamdhyas
          </h2>

          <div className="mt-6 flex items-center justify-center gap-2">
            <span className="h-1 w-20 rounded-full bg-indigo-600" />
            <span className="h-1 w-3 rounded-full bg-indigo-300" />
          </div>

          <p className="mt-4 sm:mt-6 max-w-2xl mx-auto text-sm sm:text-base text-gray-600 leading-relaxed">
            Honest experiences shared by students and interns who have learned
            and grown with us.
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="relative"
            >
              <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 md:p-12 border border-gray-100">
                <div className="absolute -top-6 left-8 sm:left-12 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FaQuoteLeft className="text-white text-lg sm:text-xl" />
                </div>

                <div className="pt-4">
                  <p className="text-lg sm:text-xl md:text-2xl font-medium leading-relaxed text-gray-800 mb-6 sm:mb-8">
                    "{testimonials[index].text}"
                  </p>

                  <div className="flex items-center gap-1 mb-6 sm:mb-8">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <FaStar
                        key={i}
                        className="text-yellow-400 text-sm sm:text-base"
                      />
                    ))}
                  </div>

                  <div className="flex items-center gap-4 sm:gap-5 pt-6 border-t border-gray-200">
                    <div className="relative">
                      <img
                        src={testimonials[index].image}
                        alt={testimonials[index].name}
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full ring-4 ring-indigo-100"
                      />
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white" />
                    </div>

                    <div>
                      <p className="font-bold text-base sm:text-lg text-gray-900">
                        {testimonials[index].name}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {testimonials[index].role}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="relative mt-10 sm:mt-12">
            {/* Arrows positioned better on mobile */}
            <button
              onClick={prev}
              className="absolute left-0 top-1/2 -translate-y-1/2 
               h-12 w-12 sm:h-14 sm:w-14 
               flex items-center justify-center 
               rounded-full bg-white/90 backdrop-blur-md
               border border-gray-300
               shadow-lg active:scale-95 transition
               z-20"
            >
              <FaChevronLeft className="text-gray-800 text-lg sm:text-xl" />
            </button>

            <button
              onClick={next}
              className="absolute right-0 top-1/2 -translate-y-1/2 
               h-12 w-12 sm:h-14 sm:w-14 
               flex items-center justify-center 
               rounded-full bg-white/90 backdrop-blur-md
               border border-gray-300
               shadow-lg active:scale-95 transition
               z-20"
            >
              <FaChevronRight className="text-gray-800 text-lg sm:text-xl" />
            </button>

            {/* Dots centered */}
            <div className="flex items-center justify-center gap-2 px-16">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`h-2.5 rounded-full transition-all ${
                    i === index ? "w-8 bg-indigo-600" : "w-2.5 bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
